import React, { PropsWithChildren, Ref, forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { ZodSchema, z } from "zod";

//types
import { FormHandle, FormProps, PyFormApi, VALIDATE_ON } from "./types";

//hooks
import { useAppForm } from "../../../hooks/useAppForm";

// utils
import { createPyFormApi } from "./utils";

//components
import { AnimatePresence, LayoutGroup } from "../AnimatePresence";
import { PyFormContext } from "./context";

/**
 * This is the main form component that is used to create a form.
 * it supplies an instance of the form context, and handles default expected form behaviour
 * To use inputs within the form, use the FormField component.
 * There are several pre-made and pre-handled inputs that can be found by prepending Form to the input name
 *
 * @example
 * <PYForm schema={schema} defaultValues={defaultValues} onSubmit={onSubmit} validateOn={validateOn}>
 *  <FormInputText id="name" label="Name" />
 * </PYForm>
 *
 * @param props - The props of the form
 * @returns The form component
 */
const PYFormInner = <TSchema extends ZodSchema>(
	{
		children,
		schema,
		defaultValues,
		onSubmit,
		validateOn = [VALIDATE_ON.ON_SUBMIT],
		id,
		dangerouslySetClassName,
	}: PropsWithChildren<FormProps<TSchema> & { onChange?: (value: z.infer<TSchema>) => void }>,
	ref: Ref<FormHandle<TSchema>>
) => {
	const formElementRef = useRef<HTMLFormElement>(null);

	const form = useAppForm({
		defaultValues,
		validators: {
			onSubmit: validateOn.includes(VALIDATE_ON.ON_SUBMIT) ? schema : undefined,
			onChange: validateOn.includes(VALIDATE_ON.ON_CHANGE) ? schema : undefined,
			onBlur: validateOn.includes(VALIDATE_ON.ON_BLUR) ? schema : undefined,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} as any,
		onSubmit: async ({ value, formApi, meta }) => {
			const resetForm = formApi.reset;
			const setError = (field: keyof z.infer<TSchema>, error: string) => {
				form.setFieldMeta(field as unknown as Parameters<typeof form.setFieldMeta>[0], (meta) => ({
					...meta,
					//this get cleared due to the validation cycle rerunning the zod validation
					//therefore we need to manually add the error to the meta
					serverError: error,
				}));
			};

			await onSubmit({
				value,
				resetForm,
				setError,
				meta: meta as Record<string, unknown>,
			});
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit({ event: e });
	};

	// Create imperative API methods
	const imperativeApi = useMemo(
		() =>
			({
				reset: form.reset,
				submit: async (meta?: Record<string, unknown>) => {
					await form.handleSubmit(meta);
				},
				getFieldValue: ((name: string) =>
					form.getFieldValue(name as Parameters<typeof form.getFieldValue>[0])) as (
					name: string
				) => z.infer<TSchema>,
				getFormValues: () => form.state.values,
				validate: async (addErrors?: boolean) => {
					if (addErrors) {
						// Mark all fields as touched so errors will display
						const values = form.state.values as Record<string, unknown>;
						Object.keys(values).forEach((fieldName) => {
							const fieldMeta = form.getFieldMeta(fieldName);
							if (fieldMeta && !fieldMeta.isTouched) {
								form.setFieldMeta(fieldName as Parameters<typeof form.setFieldMeta>[0], (prev) => ({
									...prev,
									isTouched: true,
								}));
							}
						});

						// Run form-level validation (Zod schema) which will populate field errors
						await form.validate("submit");
					}

					// Check validity using form state
					const isValid = form.state.isValid;
					return isValid;
				},
			}) as FormHandle<TSchema>,
		[form]
	);

	// Wrapped setFieldValue that ensures proper field metadata initialization
	// This works around a TanStack Form bug where setFieldValue on new fields
	// creates field metadata without errorSourceMap, causing validation errors
	const wrappedSetFieldValue = useMemo(() => {
		return <TField extends Parameters<typeof form.setFieldValue>[0]>(
			field: TField,
			...args: Parameters<typeof form.setFieldValue> extends [unknown, ...infer Rest] ? Rest : never
		) => {
			// Check if field metadata exists, if not initialize it with complete default values
			const existingMeta = form.getFieldMeta(field);
			if (!existingMeta || !existingMeta.errorSourceMap) {
				form.setFieldMeta(field, (prev: Record<string, unknown> | undefined) => ({
					isValidating: false,
					isTouched: false,
					isBlurred: false,
					isDirty: false,
					isPristine: true,
					isValid: true,
					isDefaultValue: true,
					errors: [],
					errorMap: {},
					errorSourceMap: {},
					...prev,
				}));
			}
			return form.setFieldValue(field, ...args);
		};
	}, [form]);

	// Create PyFormApi with both TanStack form API and imperative methods
	const pyFormApi = useMemo(
		() => ({
			...createPyFormApi<TSchema>(form),
			...imperativeApi,
			getFieldMeta: form.getFieldMeta,
			setFieldMeta: form.setFieldMeta,
			setFieldValue: wrappedSetFieldValue,
		}),
		[form, imperativeApi, wrappedSetFieldValue]
	);

	// Expose imperative methods through ref
	useImperativeHandle(ref, () => imperativeApi, [imperativeApi]);

	return (
		<PyFormContext.Provider
			value={
				{
					...pyFormApi,
					id,
					schema,
				} as unknown as PyFormApi<TSchema, Record<string, unknown>> & { id: string; schema: TSchema }
			}
		>
			<form id={id} onSubmit={handleSubmit} ref={formElementRef} className={dangerouslySetClassName}>
				<LayoutGroup>
					<AnimatePresence mode="sync" propagate={true}>
						{children}
					</AnimatePresence>
				</LayoutGroup>
			</form>
		</PyFormContext.Provider>
	);
};

// Export with proper generic forwardRef typing
// biome-ignore lint/suspicious/noExplicitAny: Required for generic forwardRef pattern - TanStack Form types are not fully compatible with Zod v4
export const PYForm = forwardRef(PYFormInner) as <TSchema extends ZodSchema>(
	props: PropsWithChildren<FormProps<TSchema> & { onChange?: (value: z.infer<TSchema>) => void }> & {
		ref?: Ref<FormHandle<TSchema>>;
	}
) => React.ReactElement | null;

export * from "./types";
export * from "./components";
export * from "./context";
export type { FormHandle } from "./types";
